import React from 'react'

import clsx from 'clsx'

import { IconButton, makeStyles, Tooltip, useTheme } from '@material-ui/core'
import HistoryIcon from '@material-ui/icons/History'
import TodayIcon from '@material-ui/icons/Today'

import { LoriIcon } from './Icons/LoriIcon'
import { Container } from '../../../components'
import { useLoriState } from '../../../contexts/LoriContext'
import { useHistory, useLocation } from 'react-router-dom'
import { Routes } from '../../config/routes'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import { VideoLibrary } from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
    highlighted: {
        backgroundColor: '#ffffff',
        transition: 'background-color 0.3s ease, transform 0.3s ease',
        transform: 'scale(1.05)',
        '&:hover': {
            backgroundColor: '#ffffff',
        },
        '& svg': {
            color: theme.palette.darkBlue.main,
        },
    },
    iconButton: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.white.main}`,
        marginBottom: theme.spacing(1),
    },
    '@keyframes pulse': {
        '0%': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.7)',
        },
        '50%': {
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            transform: 'scale(1.1)',
            boxShadow: '0 0 0 10px rgba(255, 255, 255, 0)',
        },
        '100%': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)',
        },
    },
    attentionGrabbing: {
        animation: '$pulse 2s infinite',
        borderRadius: '50%',
        '& svg': {
            color: theme.palette.darkBlue.main,
        },
    },
    '@keyframes unreadPulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
        },
        '70%': {
            transform: 'scale(1.08)',
            boxShadow: '0 0 0 8px rgba(244, 67, 54, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
        },
    },
    unreadHighlighted: {
        backgroundColor: theme.palette.error.main,
        borderRadius: '50%',
        transition: 'background-color 0.3s ease, transform 0.3s ease',
        animation: '$unreadPulse 1.6s infinite',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
        '& svg': {
            color: theme.palette.common.white,
        },
    },
}))

interface Props {
    selectedChannel: LoriChannel
    setSelectedChannel: (channel: LoriChannel) => void
}

export const ChannelSelector = (props: Props) => {
    const { selectedChannel, setSelectedChannel } = props
    const theme = useTheme()
    const history = useHistory()
    const location = useLocation()
    const classes = useStyles()
    const {
        highlightWTIcon,
        setHighlightWTIcon,
        unreadLoriMessages,
    } = useLoriState()

    const isDailyActivityActive = location.pathname.startsWith(
        Routes.apartment.dailyActivity.root,
    )
    const isRecordsActive = location.pathname.startsWith(
        Routes.apartment.records.root,
    )

    return (
        <Container
            className={classes.container}
            style={{ paddingBottom: theme.spacing(1) }}
        >
            <Tooltip title="Lori">
                <IconButton
                    onClick={() => {
                        if (selectedChannel === 'lori') {
                            setSelectedChannel('none')
                        } else {
                            setSelectedChannel('lori')
                        }
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]: selectedChannel === 'lori',
                        [classes.unreadHighlighted]:
                            selectedChannel !== 'lori' &&
                            unreadLoriMessages > 0,
                    })}
                >
                    <LoriIcon
                        width={24}
                        height={24}
                        color={
                            selectedChannel === 'lori'
                                ? theme.palette.darkBlue.main
                                : theme.palette.primary.contrastText
                        }
                    />
                </IconButton>
            </Tooltip>

            <Tooltip title="History">
                <IconButton
                    onClick={() => {
                        if (selectedChannel === 'history') {
                            setSelectedChannel('none')
                        } else {
                            setSelectedChannel('history')
                        }
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]: selectedChannel === 'history',
                    })}
                >
                    <HistoryIcon
                        style={{ fontSize: 30 }}
                        htmlColor={theme.palette.primary.contrastText}
                    />
                </IconButton>
            </Tooltip>

            <Tooltip title="Video">
                <IconButton
                    onClick={() => {
                        setSelectedChannel('none')
                        setHighlightWTIcon(false)
                        history.push(Routes.shared.help)
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]:
                            selectedChannel === 'walkthrough',
                        [classes.attentionGrabbing]: highlightWTIcon,
                    })}
                >
                    <VideoLibrary
                        style={{ fontSize: 30 }}
                        htmlColor={theme.palette.primary.contrastText}
                    />
                </IconButton>
            </Tooltip>

            <Tooltip title="Daily Activity">
                <IconButton
                    onClick={() => {
                        setSelectedChannel('none')
                        history.push(Routes.apartment.dailyActivity.root)
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]: isDailyActivityActive,
                    })}
                >
                    <TodayIcon
                        style={{ fontSize: 28 }}
                        htmlColor={
                            isDailyActivityActive
                                ? theme.palette.darkBlue.main
                                : theme.palette.primary.contrastText
                        }
                    />
                </IconButton>
            </Tooltip>

            <Tooltip title="Records">
                <IconButton
                    onClick={() => {
                        setSelectedChannel('none')
                        history.push(Routes.apartment.records.root)
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]: isRecordsActive,
                    })}
                >
                    <FolderOpenIcon
                        style={{ fontSize: 28 }}
                        htmlColor={
                            isRecordsActive
                                ? theme.palette.darkBlue.main
                                : theme.palette.primary.contrastText
                        }
                    />
                </IconButton>
            </Tooltip>

            {/* <Tooltip title="Notifications">
                <IconButton
                    onClick={() => {
                        if (selectedChannel === 'notifications') {
                            setSelectedChannel('none')
                        } else {
                            setSelectedChannel('notifications')
                        }
                    }}
                    size="small"
                    className={clsx(classes.iconButton, {
                        [classes.highlighted]:
                            selectedChannel === 'notifications',
                    })}
                >
                    <NotificationsIcon
                        style={{ fontSize: 30 }}
                        htmlColor={theme.palette.primary.contrastText}
                    />
                </IconButton>
            </Tooltip> */}
        </Container>
    )
}
